import type { Leg } from 'types/Leg.ts';
import type { Package } from 'types/Package.ts';
import type { Participant } from 'types/Participant.ts';
import type { User } from '#types/User.ts';

export const SHIPMENT_STATES = ['draft', 'confirmed', 'cancelled'] as const;
export type SHIPMENT_STATE = (typeof SHIPMENT_STATES)[number];

export const SHIPMENT_STATUSES = ['draft', 'pending', 'transiting', 'delivered', 'cancelled'] as const;
export type SHIPMENT_STATUS = (typeof SHIPMENT_STATUSES)[number];

export const SHIPMENT_COLORS = ['grey', 'green', 'yellow', 'red', 'blue'];
export type SHIPMENT_COLOR = (typeof SHIPMENT_COLORS)[number];

export type ShipperAndConsigneeInfo = {
  date?: string;
  name?: string;
  comments?: string;
  signature?: string;
  signer_name?: string;
  origin_actual_datetime: string | null;
  origin_address_name: string | null;
  origin_address: string;
  destination_actual_datetime: string | null;
  destination_address_name: string | null;
  destination_address: string;
  origin_is_shipper: boolean;
  destination_is_consignee: boolean;
};

export type ShipmentRow = {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  owner_id: string;
  logistics_ex_id: string;
  logistics_imported_at: string | null;
  metrics_ex_id: string | null;
  metrics_imported_at: string | null;
  bc_sync_at: string | null;
  bc_sync_pending: boolean;
  customer_id: string;
  shipper_id: string;
  consignee_id: string;
  shipper_order_reference: string | null;
  service_type: string | null;
  is_dangerous_goods: boolean;
  is_excepted_quantities: boolean;
  is_limited_quantity: boolean;
  instructions: string | null;
  terms_url: string | null;
  metadata: string | null;
  labels: string[];
  state: SHIPMENT_STATE;
  // computed on the fly in the repository
  status: SHIPMENT_STATUS;
  collection_address_name: string | null; // null for draft shipments having no legs
  collection_city: string | null; // null for draft shipments having no legs
  collection_country: string | null; // null for draft shipments having no legs
  collection_postcode: string | null; // null for draft shipments having no legs
  collection_datetime: string | null; // null for draft shipments having no legs
  collection_tz: string | null; // null for draft shipments having no legs
  delivery_address_name: string | null; // null for draft shipments having no legs
  delivery_city: string | null; // null for draft shipments having no legs
  delivery_country: string | null; // null for draft shipments having no legs
  delivery_postcode: string | null; // null for draft shipments having no legs
  delivery_datetime: string | null; // null for draft shipments having no legs
  delivery_tz: string | null; // null for draft shipments having no legs
  actual_delivery_datetime: string | null;
  next_eta: string | null;
  delayed: boolean;
  unassigned: boolean;
  legs_count: number;
  supplier_reference_id: string | null;
  citysprint_booking_number: string | null;
  admin_users: string[];
  read_users: string[];
};

export type ShipmentRowInsert = Partial<
  Omit<ShipmentRow, 'state' | 'status' | 'collection_country' | 'collection_datetime' | 'collection_tz' | 'delivery_country' | 'delivery_datetime' | 'delivery_tz' | 'delayed'>
> &
  NonNullable<Pick<ShipmentRow, 'owner_id' | 'logistics_ex_id' | 'customer_id' | 'shipper_id' | 'consignee_id' | 'state'>>;

export type ShipmentRowUpdate = Partial<Omit<ShipmentRow, 'id'>>;

// user joined with relations
export type Shipment = ShipmentRow & {
  owner: User;
  customer: Participant;
  shipper: Participant;
  consignee: Participant;
  legs: Leg[];
  packages: Package[];
};
